<!-- Opened with panelClass ["base-modal-panel", "nova-companion-chat-panel"] - the
     second class is what anchors this to the bottom of the viewport instead of
     centering it like every other base-modal dialog; see cdk-theme.scss. height="standard"
     (not "tall") deliberately leaves room above the card for that bottom anchoring to
     read as a sheet rather than a near-full-screen centered modal. -->
<app-base-modal
  backgroundColor="var(--color-cream)"
  gradientFrom="transparent"
  gradientTo="transparent"
  [hasCustomHeader]="true"
  size="standard"
  height="standard"
  (closed)="onSheetClose()"
>
  <!-- Header reflects TODAY'S Safety Buffer band (green/amber/red), set from
       NovaCompanionChatService.getBufferBand() on open. "no-score" - no score today, or the
       context call failed - applies none of the three and leaves the base parchment header. -->
  <div
    header
    class="nova-companion-chat__head"
    flipCoachTarget="nova.buffer_header"
    [class.nova-companion-chat__head--green]="bufferBand === 'green' && !showFirstUse"
    [class.nova-companion-chat__head--amber]="bufferBand === 'amber' && !showFirstUse"
    [class.nova-companion-chat__head--red]="bufferBand === 'red' && !showFirstUse"
  >
    <img class="nova-companion-chat__avatar" src="assets/nova/nova-mascot.png" alt="" aria-hidden="true" />
    <div class="nova-companion-chat__head-info">
      <div class="nova-companion-chat__head-name">nova</div>
      <div class="nova-companion-chat__head-sub">Your flip* guide</div>
    </div>

    <!-- FLIP-158 voice toggle. Lives in the header rather than the composer so it stays put
         and keeps reading as "voice chat is on" while the composer below swaps between the
         text input and the listening/speaking status bar.
         The --paired modifier hands its close-button clearance over to the FLIP-234 minimise
         button below, which only renders in voice mode and is the rightmost control when it
         does; without it both would carry the clearance and open a gap between them. -->
    <button
      *ngIf="!showFirstUse"
      type="button"
      class="nova-companion-chat__voice-toggle"
      flipCoachTarget="nova.voice_chat"
      [class.nova-companion-chat__voice-toggle--active]="isVoiceActive"
      [class.nova-companion-chat__voice-toggle--paired]="isVoiceActive"
      [attr.aria-pressed]="isVoiceActive"
      (click)="onVoiceToggleSelected()"
    >
      <!-- Inactive: the waveform glyph, static. The asset paints with currentColor and
           svg-icon.parchment sets `color` on the host, which the inlined <svg> inherits. This
           has to be <svg-icon> rather than <img> for that to work at all: an <img> renders in
           its own document and inherits nothing from the page. -->
      <svg-icon
        *ngIf="!isVoiceActive"
        src="assets/icons/icon-sound.svg"
        [svgStyle]="{ 'width.px': 18, 'height.px': 12 }"
        class="nova-companion-chat__voice-toggle-wave parchment"
        aria-hidden="true"
      ></svg-icon>
      <!-- Active: the design swaps the waveform for a solid dot. -->
      <span
        *ngIf="isVoiceActive"
        class="nova-companion-chat__voice-toggle-dot"
        aria-hidden="true"
      ></span>
      <!-- aria-live so a screen reader hears voice mode turn on and off; the gradient and
           label change alone aren't perceivable to one. -->
      <span class="nova-companion-chat__voice-toggle-label" aria-live="polite">
        {{ voiceToggleLabel }}
      </span>
    </button>

    <!-- FLIP-234 minimise. Inside *ngIf="isVoiceActive" because the requirement is that minimise
         is offered in voice mode only: there is nothing for a text conversation to keep running
         in the background, so a chevron on the text composer would promise something that does
         not exist. Sits after the voice toggle, which already carries that toggle's own
         margin-right clearing base-modal's absolutely-positioned close button - so this one
         needs no clearance of its own, it is the toggle that moves left to make room. -->
    <button
      *ngIf="isVoiceActive"
      #voiceMinimizeBtn
      type="button"
      class="nova-companion-chat__minimize"
      title="Minimise Nova, keep voice chat running"
      aria-label="Minimise Nova, keep voice chat running"
      (click)="minimizeVoice()"
    ></button>
  </div>

  <!-- Message thread. Its CONTENTS are hidden while voice mode runs - a voice conversation is
       eyes-free, and text scrolling past while someone talks invites reading instead of
       listening - but the container stays, so the sheet does not collapse to a header and a pill
       the moment someone starts talking. The thread keeps building underneath and is revealed,
       scrolled to the newest turn, when voice mode ends. -->
  <!-- FLIP-389 N02 (Figma node 33:8935): the first-use panel replaces the chat area - the thread and,
       via the host class, the composer - under Nova's header, which drops Voice Chat while it is up.
       The header's close closes the panel and reveals the chat. Not part of the conversation: the
       thread is only hidden, so nothing is lost. -->
  <app-nova-first-use-panel *ngIf="showFirstUse" (closed)="n02.onClosePressed()"></app-nova-first-use-panel>

  <div
    class="nova-companion-chat__thread"
    [class.nova-companion-chat__thread--hushed]="isVoiceActive"
    [style.display]="showFirstUse ? 'none' : null"
  >
    <!-- Exempt from the hush rule, and the only thing in the sheet while voice runs. Without it
         the space where the conversation was reads as the app having lost it. -->
    <p *ngIf="isVoiceActive" class="nova-companion-chat__voice-note">
      Your full conversation will be here when you end voice chat.
    </p>
    <ng-container *ngFor="let item of thread; let itemIndex = index">
      <!-- Marks where one conversation ended and the next began. A separator rather than a
           heading element: it labels a break in the flow, and announcing it as a heading would
           put a screen-reader user into a document outline the thread does not otherwise have. -->
      <div *ngIf="item.kind === 'divider'" class="nova-companion-chat__divider" role="separator">
        <span>{{ item.text }}</span>
      </div>

      <div
        *ngIf="item.kind === 'message'"
        class="nova-companion-chat__row"
        [class.nova-companion-chat__row--user]="item.role === 'user'"
      >
        <div *ngIf="item.role !== 'user'" class="nova-companion-chat__avatar-mini">
          <img src="assets/nova/nova-mascot.png" alt="Nova" />
        </div>
        <!-- No word-by-word highlight: the thread is hushed for the whole of voice mode, so a
             bubble dimming itself would be rendering into a hidden container. -->
        <div class="nova-companion-chat__bubble">{{ item.text }}</div>
      </div>

      <!-- The handoff's navtoast: a direct child of the thread, not wrapped in a __row, so it
           sits outside the avatar/bubble flex and aligns with Nova's bubbles via its own
           margin-left (same inset the typing indicator uses). Unlike the handoff's static
           div, ours is a real button - it routes. -->
      <button
        *ngIf="item.kind === 'nav'"
        type="button"
        class="nova-companion-chat__nav-toast"
        title="Navigate to {{ item.text }}"
        aria-label="Navigate to {{ item.text }}"
        (click)="onNavChipClicked(item.navTarget, item.navResolved)"
      >
        ↗ Explore: {{ item.text }}
      </button>

      <div *ngIf="item.kind === 'typing'" class="nova-companion-chat__typing" aria-label="Nova is typing">
        <span></span><span></span><span></span>
      </div>
    </ng-container>

    <div *ngIf="chipsLoading" class="nova-companion-chat__typing" aria-label="Loading suggestions">
      <span></span><span></span><span></span>
    </div>

    <div *ngIf="showChips" class="nova-companion-chat__chips">
      <button
        *ngFor="let chip of chips"
        type="button"
        class="nova-companion-chat__chip"
        (click)="onChipClicked(chip)"
      >
        {{ chip }}
      </button>
    </div>
  </div>

  <!-- Composer -->
  <div actions class="nova-companion-chat__composer">
    <!-- Voice mode carries no caption: it is eyes-free, and the status bar below says whose turn
         it is. The full conversation is in the thread when voice mode ends. -->

    <!-- FLIP-158 voice mode. The status bar REPLACES the composer rather than sitting beside
         it: while voice mode runs there is no typing, no send button and no dictate overlay,
         so the same *ngIf-exclusivity rule the dictation controls follow applies here too. -->
    <!-- A plain container, not a button: the stop control inside it is the button, exactly as in
         the dictation overlay below. Nesting one in the other would be invalid markup, and a
         single outer button would give a screen reader one control whose label swallows the
         status text and the meter. -->
    <div
      *ngIf="isVoiceActive"
      class="nova-companion-chat__voice-bar"
      [class.nova-companion-chat__voice-bar--speaking]="voiceState === 'speaking'"
    >
      <!-- aria-live so a screen reader announces the turn changing hands; the visual state
           change alone isn't perceivable to one. -->
      <span class="nova-companion-chat__voice-bar-label" aria-live="polite">
        {{ voiceStatusLabel }}
      </span>
      <!-- Live level meter, driven by whoever is talking: the microphone on the user's turn,
           Nova's own playback on hers. Never a canned animation - heights that don't come from
           real audio would claim someone is speaking when the room is silent. Bars are
           inline-styled because the value changes every frame - a class can't carry it. -->
      <!-- Hidden rather than removed while silent. Nothing moves either way now the bar is
           space-between and the strip takes the slack, but *ngIf on the signal would tear down
           sixty-odd bars and the ResizeObserver watching them several times a sentence, and
           remeasure the width on the way back. "thinking" is a different case: neither source is
           running, so the strip is genuinely absent rather than idle. -->
      <!-- Left mounted while docked, deliberately. This is now the only #voiceWave in the app -
           the minimised launcher draws its own copy from published levels, in another component -
           so there is no second element for the ViewChild setter to contend with, and keeping
           this one alive means the measured bar count survives a dock/undock round trip. The
           sheet is display:none while docked, so its ResizeObserver reports 0 and
           setVoiceBarCount early-returns on width <= 0 rather than collapsing the count. -->
      <span
        #voiceWave
        *ngIf="voiceState === 'listening' || voiceState === 'speaking'"
        class="nova-companion-chat__voice-bar-wave"
        [class.nova-companion-chat__voice-bar-wave--idle]="!voiceHasSignal"
        aria-hidden="true"
      >
        <span
          *ngFor="let level of voiceLevels; trackBy: trackVoiceBar"
          [style.height.%]="level"
        ></span>
      </span>
      <!-- The same stop control dictation uses, for the same reason: a mic glyph here pointed at
           starting dictation, which is the opposite of what pressing it does. -->
      <button
        type="button"
        class="nova-companion-chat__voice-bar-stop"
        aria-label="End voice chat"
        (click)="stopVoiceMode()"
      ></button>
    </div>

    <div *ngIf="!isVoiceActive" class="nova-companion-chat__input-wrapper">
      <textarea
        #inputBox
        class="nova-companion-chat__input"
        [class.nova-companion-chat__input--multiline]="isInputMultiline"
        rows="1"
        placeholder="Message Nova..."
        [(ngModel)]="inputText"
        (input)="onInputResize()"
        (transitionend)="onComposerTransitionEnd($event)"
        (keydown)="onInputKeydown($event)"
        [disabled]="isListening"
      ></textarea>

      <!-- Dictation state: overlay, mic button, and send button are mutually exclusive
           via *ngIf (never siblings sharing the same stacking box) - see this
           component's header comment for why that's the actual fix for the handoff's
           z-index/layering bug, not just an added z-index value. -->
      <div *ngIf="isListening" class="nova-companion-chat__dictate-overlay">
        <!-- Decorative, not amplitude-driven. Bar heights and phases come from waveBars so
             the wave spans the full pill with the irregular density the design shows. -->
        <div class="nova-companion-chat__dictate-wave" aria-hidden="true">
          <span
            *ngFor="let bar of waveBars"
            [style.height.%]="bar.heightPercent"
            [style.animation-delay.ms]="bar.delayMs"
          ></span>
        </div>
        <span class="nova-companion-chat__dictate-timer">{{ dictateTimerLabel }}</span>
        <button
          type="button"
          class="nova-companion-chat__stop-dictate"
          aria-label="Stop dictating"
          (click)="stopDictation()"
        ></button>
      </div>

      <button
        *ngIf="!isListening && !hasText"
        type="button"
        class="nova-companion-chat__mic-btn"
        title="Dictate"
        flipCoachTarget="nova.dictation"
        (click)="onMicSelected()"
      >
        <svg-icon src="assets/icons/icon-mic.svg"
        [svgStyle]="{ 'width.px': 14, 'height.px': 20 }"
        class="sagebrush"></svg-icon>
      </button>

      <button
        *ngIf="!isListening && hasText"
        type="button"
        class="nova-companion-chat__send-btn"
        aria-label="Send"
        [disabled]="sending"
        (click)="onSend()"
      >
        <svg-icon src="assets/icons/icon-next.svg"
        [svgStyle]="{ 'width.px': 9, 'height.px': 14 }"
        class="parchment"></svg-icon>
      </button>
    </div>
  </div>
</app-base-modal>
